import React, { useCallback, useEffect, useRef, useState } from "react";
import { X } from "@phosphor-icons/react";
import { toast, Toaster } from "react-hot-toast";
import ImageView from "../ImageView";
import VerificationIdSlots from "../VerificationIdSlots";
import useVerificationIdSlots from "../../hooks/useVerificationIdSlots";
import { ALLOWED_IMAGE_TYPES, uploadGuestImage } from "../../api/uploadImage";
import { lookupUserByContact } from "../../api/users";
import { parseVerificationIds } from "../../api/imageCache";
import { DUPLICATE_CONTACT_ERROR, isContactTaken } from "../../api/guestContacts";

const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

// Comparable snapshot of everything the form can change, so typed-but-not-
// submitted edits are visible to the parent.
const formSnapshot = (form, verification) =>
    JSON.stringify({
        name: form.name || "",
        contact: form.contact || "",
        countryCode: form.countryCode || "91",
        email: form.email || "",
        address: form.address || "",
        manual_verification: Boolean(form.manual_verification),
        is_minor: Boolean(form.is_minor),
        verification_id: verification || "",
    });

const emptyGuestForm = {
    name: "",
    contact: "",
    countryCode: "91",
    manual_verification: false,
    email: "",
    address: "",
    is_minor: false,
};

export default function ManageRoomGuestFields(props) {
    const {
        guestData,
        guests,
        setGuests,
        activeGuest,
        setActiveGuest,
        buttonDisabled,
        setButtonDisabled,
        isFormOpen,
        setIsFormOpen,
        setGuestFormDirty,
        otherRoomContacts,
    } = props;

    const isEditing = activeGuest !== -1;

    const [guestForm, setGuestForm] = useState(emptyGuestForm);
    const [errors, setErrors] = useState({});
    const [previewUrl, setPreviewUrl] = useState("");

    const {
        slots,
        setSlotLoading,
        setSlotImage,
        clearSlot,
        resetSlots,
        verificationValue,
        hasAnyId,
        hasFrontId,
        hasBackId,
        isUploading,
    } = useVerificationIdSlots(isEditing ? guestData?.verification_id : "", activeGuest);

    // Load the selected guest into the form. The ID slots come straight off
    // guestData via the hook, so they never drift out of sync with the record.
    const baselineSnapshot = useRef(formSnapshot(emptyGuestForm, ""));

    useEffect(() => {
        setErrors({});

        if (!isEditing || !guestData) {
            setGuestForm(emptyGuestForm);
            baselineSnapshot.current = formSnapshot(emptyGuestForm, "");
            return;
        }

        const contact = guestData.contact?.toString() || "";

        const loadedForm = {
            name: guestData.name || "",
            contact: contact.substring(2),
            countryCode: contact.substring(0, 2) || "91",
            manual_verification: guestData.manual_verification || false,
            email: guestData.email || "",
            address: guestData.address || "",
            is_minor: guestData.is_minor || false,
        };

        setGuestForm(loadedForm);
        baselineSnapshot.current = formSnapshot(
            loadedForm,
            parseVerificationIds(guestData.verification_id).slice(0, 2).join(","),
        );
    }, [guestData, isEditing]);

    // Report typed-but-not-submitted edits upward so closing the modal can warn.
    const isFormDirty = isFormOpen && formSnapshot(guestForm, verificationValue) !== baselineSnapshot.current;

    useEffect(() => {
        setGuestFormDirty(isFormDirty);
    }, [isFormDirty, setGuestFormDirty]);

    // Prefill a brand new guest from their Alfred account. Add mode only —
    // while editing, the booking record is authoritative.
    const prefilledContact = useRef("");
    useEffect(() => {
        if (isEditing || guestForm.is_minor || guestForm.contact.length !== 10) {
            return undefined;
        }

        const fullContact = `${guestForm.countryCode || "91"}${guestForm.contact}`;
        if (prefilledContact.current === fullContact) {
            return undefined;
        }

        let active = true;
        const timer = setTimeout(async () => {
            const user = await lookupUserByContact(fullContact);
            if (!active || !user) {
                return;
            }
            prefilledContact.current = fullContact;
            setGuestForm((prev) => ({
                ...prev,
                name: prev.name || user.name || "",
                email: prev.email || user.email || "",
                address: prev.address || user.address || "",
            }));
        }, 400);

        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [guestForm.contact, guestForm.countryCode, guestForm.is_minor, isEditing]);

    const validate = useCallback(() => {
        const nextErrors = {};

        if (!guestForm.name.trim()) {
            nextErrors.name = "Name is required";
        } else if (!guestForm.is_minor && /\d/.test(guestForm.name)) {
            nextErrors.name = "Name cannot contain digits";
        }

        if (!guestForm.is_minor) {
            if (!/^\d{10}$/.test(guestForm.contact.trim())) {
                nextErrors.contact = "Contact must be a 10 digit number";
            } else if (
                isContactTaken(
                    `${guestForm.countryCode || "91"}${guestForm.contact}`,
                    guests,
                    activeGuest,
                    otherRoomContacts,
                )
            ) {
                nextErrors.contact = DUPLICATE_CONTACT_ERROR;
            }
            if (!guestForm.address.trim()) {
                nextErrors.address = "Address is required";
            }
            if (!guestForm.email.trim()) {
                nextErrors.email = "Email is required";
            } else if (!EMAIL_PATTERN.test(guestForm.email)) {
                nextErrors.email = "Invalid email address";
            }
            if (!guestForm.manual_verification) {
                if (!hasFrontId) {
                    nextErrors.id1 = "Front ID is required";
                }
                if (!hasBackId) {
                    nextErrors.id2 = "Back ID is required";
                }
                if (!hasAnyId) {
                    nextErrors.verification_id =
                        "Either provide both verification IDs or tick 'Verified by Receptionist'";
                }
            }
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    }, [guestForm, guests, activeGuest, otherRoomContacts, hasAnyId, hasBackId, hasFrontId]);

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;

        if (type === "checkbox") {
            setGuestForm((prev) => ({ ...prev, [name]: checked }));
            setErrors((prev) => {
                if (name === "is_minor") {
                    return {};
                }
                const next = { ...prev };
                if (checked) {
                    delete next.verification_id;
                    delete next.id1;
                    delete next.id2;
                }
                return next;
            });
            return;
        }

        setGuestForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleSelectFile = async (index, event) => {
        const file = event.target.files?.[0];
        event.target.value = "";

        if (!file) {
            return;
        }
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            setErrors((prev) => ({
                ...prev,
                [`id${index + 1}`]: "Only .jpg, .jpeg, .png and .webp images are allowed",
            }));
            return;
        }

        setSlotLoading(index, true);
        setButtonDisabled(true);
        try {
            const { key, previewUrl: uploadedPreview } = await uploadGuestImage(file);
            setSlotImage(index, key, uploadedPreview);
            setErrors((prev) => ({ ...prev, [`id${index + 1}`]: "", verification_id: "" }));
            toast.success("Image uploaded successfully");
        } catch (error) {
            console.error(error);
            setSlotLoading(index, false);
            toast.error("Could not upload the image, please try again");
        } finally {
            setButtonDisabled(false);
        }
    };

    const buildGuest = (existing) => {
        if (guestForm.is_minor) {
            return {
                ...existing,
                name: guestForm.name.trim(),
                is_minor: true,
                contact: undefined,
                verification_id: "",
                manual_verification: false,
                email: "",
                address: "",
            };
        }

        return {
            ...existing,
            name: guestForm.name.trim(),
            contact: parseInt(`${guestForm.countryCode || "91"}${guestForm.contact}`, 10),
            email: guestForm.email.trim(),
            address: guestForm.address.trim(),
            verification_id: verificationValue,
            manual_verification: guestForm.manual_verification,
            is_minor: false,
        };
    };

    const closeForm = () => {
        setActiveGuest(-1);
        setIsFormOpen(false);
        setErrors({});
    };

    const handleSubmit = () => {
        if (!validate()) {
            return;
        }

        if (isEditing) {
            setGuests(guests.map((guest, index) => (index === activeGuest ? buildGuest(guest) : guest)));
        } else {
            setGuests([...guests, buildGuest({})]);
            setGuestForm(emptyGuestForm);
            resetSlots();
            prefilledContact.current = "";
        }

        setActiveGuest(-1);
        setIsFormOpen(false);
    };

    const fieldError = (key) =>
        errors[key] ? <span className="text-sm text-[#FF7378]">{errors[key]}</span> : null;

    const submitLabel = guestForm.is_minor
        ? isEditing
            ? "Update Kid"
            : "Add Kid"
        : isEditing
          ? "Update Guest"
          : "Add Guest";

    return (
        <>
            <div className={`p-2 sticky top-0 ${isFormOpen ? "block" : "hidden"}`}>
                <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between border-b pb-2">
                        <h3 className="font-semibold text-[#262B33]">
                            {isEditing ? "Edit Guest" : "Add Guest"}
                        </h3>
                        <button
                            type="button"
                            title="Close"
                            className="hover:bg-[#F2F7FF] p-1.5 border-[1.5px] rounded-md"
                            onClick={closeForm}
                        >
                            <X weight="bold" size={16} />
                        </button>
                    </div>

                    <div>
                        <label className="flex items-center justify-between p-3 bg-[#FFF8F0] border border-[#FED7AA] rounded-lg cursor-pointer">
                            <span className="flex items-center gap-2 font-medium text-[#9A3412]">
                                🧒 This guest is a kid (minor)
                            </span>
                            <span className="relative inline-flex">
                                <input
                                    type="checkbox"
                                    name="is_minor"
                                    className="sr-only peer"
                                    checked={guestForm.is_minor}
                                    onChange={handleChange}
                                />
                                <span className="w-10 h-6 bg-gray-300 peer-checked:bg-[#FF7378] rounded-full relative transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-5 after:h-5 after:bg-white after:rounded-full after:shadow after:transition-transform peer-checked:after:translate-x-4"></span>
                            </span>
                        </label>
                        {guestForm.is_minor && (
                            <p className="text-xs text-gray-500 mt-1.5">
                                Only the kid's name is needed — contact, email, address and ID are not
                                required.
                            </p>
                        )}
                    </div>

                    {!guestForm.is_minor && (
                        <div>
                            <h4>Contact</h4>
                            <div className="flex p-2 border border-[#BFC4CC] rounded-md">
                                <select
                                    className="outline-none"
                                    name="countryCode"
                                    value={guestForm.countryCode || "91"}
                                    onChange={handleChange}
                                >
                                    <option value="91">+91</option>
                                    <option value="92">+92</option>
                                    <option value="93">+93</option>
                                </select>
                                <input
                                    type="text"
                                    className="outline-none w-full"
                                    name="contact"
                                    maxLength={10}
                                    value={guestForm.contact}
                                    onChange={handleChange}
                                />
                            </div>
                            {fieldError("contact")}
                        </div>
                    )}

                    <div>
                        <h4>Name</h4>
                        <input
                            type="text"
                            className="w-full p-2 border border-[#BFC4CC] rounded-lg"
                            name="name"
                            value={guestForm.name}
                            onChange={handleChange}
                        />
                        {fieldError("name")}
                    </div>

                    {!guestForm.is_minor && (
                        <>
                            <div>
                                <h4>Email</h4>
                                <input
                                    type="email"
                                    className="w-full p-2 border border-[#BFC4CC] rounded-lg"
                                    name="email"
                                    value={guestForm.email}
                                    onChange={handleChange}
                                />
                                {fieldError("email")}
                            </div>

                            <div>
                                <h4>Address</h4>
                                <input
                                    type="text"
                                    className="w-full p-2 border border-[#BFC4CC] rounded-lg"
                                    name="address"
                                    value={guestForm.address}
                                    onChange={handleChange}
                                />
                                {fieldError("address")}
                            </div>

                            <div>
                                <h4>Verification ID</h4>
                                {errors.id1 && <p className="text-sm text-[#FF7378]">{errors.id1}</p>}
                                {errors.id2 && <p className="text-sm text-[#FF7378]">{errors.id2}</p>}
                                <VerificationIdSlots
                                    slots={slots}
                                    disabled={buttonDisabled}
                                    onSelectFile={handleSelectFile}
                                    onClear={clearSlot}
                                    onPreview={setPreviewUrl}
                                />
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    className="mr-2 cursor-pointer"
                                    id="manualVerificationRoomGuest"
                                    name="manual_verification"
                                    checked={guestForm.manual_verification}
                                    onChange={handleChange}
                                />
                                <label
                                    htmlFor="manualVerificationRoomGuest"
                                    className="text-md cursor-pointer"
                                >
                                    ID Manually Verified By Receptionist
                                </label>
                            </div>
                            {errors.verification_id && (
                                <p className="text-sm text-[#FF7378]">{errors.verification_id}</p>
                            )}
                        </>
                    )}

                    <div className="flex gap-2">
                        <button
                            type="button"
                            className="w-1/3 text-center p-2 border border-[#BFC4CC] rounded-md font-medium hover:bg-[#F2F7FF]"
                            onClick={closeForm}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className={`w-2/3 text-center p-2 bg-[#FF7378] text-white font-medium rounded-md transition-colors hover:bg-[#f0575d] ${
                                buttonDisabled || isUploading
                                    ? "bg-[#FFF2F2] text-[#FF7378] cursor-not-allowed hover:bg-[#FFF2F2]"
                                    : ""
                            }`}
                            disabled={buttonDisabled || isUploading}
                            onClick={handleSubmit}
                        >
                            {submitLabel}
                        </button>
                    </div>
                </div>
            </div>

            <Toaster
                position="bottom-right"
                reverseOrder={false}
                transition="Bounce"
                style={{ marginBottom: "25%" }}
            />

            {previewUrl && (
                <ImageView
                    imgModal={{ imgUrl: previewUrl, openModal: true }}
                    setImgModal={() => setPreviewUrl("")}
                />
            )}
        </>
    );
}
